'use strict';
const trackingUtils = require('../../utils/tracking.utils.js');
const SESSION_STORAGE_NAG_CLASS_PREFIX = 'nagClass_';
const SESSION_STORAGE_NAG_INSTANCE_PREFIX = 'nagInstance_';
const {trackOncePerPolicy} = require('od-modules/track_events/track.frontevents.additional.products');
const {trackClickEvent} = require('od-modules/track_events/track.frontevents.utils');

/**
 * @class
 * @param {Object} options - Options
 * @param {boolean} [options.doNotShowIfSameNagClassAlreadyShown=true] if true and a nag of the same class has
 * already be shown previously this nag instance will not be displayed anymore,
 * @param {boolean} [options.showThisInstanceJustOnce=true]
 * @param {string} options.nagClass - the nag class used in order to save nag class data in session storage.
 * Note: if not defined doNotShowIfSameNagClassAlreadyShown value will be ignored.
 * @param {string} options.instanceID - the nag instance id used in order to save nag instance data in session storage
 * @param {Object} options.selectors - nag selectors
 * @param {Object} [options.selectors.acceptButtonSelector=.nag_btn_accept] - nag accept button selectors
 * @param {Object} [options.selectors.rejectButtonSelector=.nag_btn_reject] - nag reject button selectors
 * @param {Object} [options.template] - can be both a function or an html template string
 * @param {Object} [options.tracking] - tracking data
 * @param {string} [options.tracking.page] - tracking page event
 * @param {string} [options.tracking.accept] - track event on nag accept
 * @param {string} [options.tracking.reject] - track event on nag reject
 * @param {string} [options.tracking.appears] - track event on nag shown
 * @param {Object} options.translations - nag translations object that will be applied to template
 * @param {Object} [options.transitions] - transition methods
 * @param {function} [options.transitions.accept] - transition method to call when the nag is accepted (before dismissing)
 * @param {$|WebElement} $parent - Container where nag is going to be appended
 * @param {Object} [options.label] - label for onload data mesh event
 * @param {Object} [options.actionLabel] - label for action click for data mesh event
 * @param {Object} [options.ignoreLabel] - label for ignore click for data mesh event
 * @param {Object} [options.className] - label for class for data mesh event
 */
const Nag = function(options, $parent) {
    $parent = $parent || document.body;
    this.$parent = ($parent instanceof $) ? $parent : $($parent);

    options = Odigeo._.merge({
        selectors: {
            acceptButtonSelector: '.nag_btn_accept',
            rejectButtonSelector: '.nag_btn_reject',
        },
        template: '',
        doNotShowIfSameNagClassAlreadyShown: true,
        showThisInstanceJustOnce: true,
        nagClass: '',
        instanceID: '',
        label: '',
        actionLabel: '',
        ignoreLabel: '',
        className: '',
        tracking: {
            accept: '',
            reject: '',
            appears: '',
        },
        transitions: {
            accept: Nag.defaultTransition,
        },
        translations: {
            image: '',
            title: '',
            subtitle: '',
            subtitleBold: '',
            button1: '',
            button2: '',
        },
        offer: {},
    }, options);

    this.type = null;
    this.translations = options.translations;
    this.selectors = options.selectors;
    this.templateBuilder = Odigeo._.isFunction(options.template) ?
        options.template :
        Odigeo._.template(options.template, Odigeo.Utils.getTmplSettings());

    this.instanceID = options.instanceID;
    this.sessionStorageInstanceID = Odigeo._.isEmpty(this.instanceID) ?
        null : SESSION_STORAGE_NAG_INSTANCE_PREFIX + options.instanceID;
    this.class = options.nagClass;
    this.sessionStorageClass = Odigeo._.isEmpty(this.class) ?
        null : SESSION_STORAGE_NAG_CLASS_PREFIX + this.class;

    this.transitions = options.transitions;
    this.label = options.label;
    this.actionLabel = options.actionLabel;
    this.ignoreLabel = options.ignoreLabel;
    this.className = options.className;
    this.offer = options.offer;

    this.updateTracking(options.tracking);

    this.initSessionStorageData();

    this.showThisInstanceJustOnce = options.showThisInstanceJustOnce === true;
    this.doNotShowIfSameNagClassAlreadyShown = !Odigeo._.isEmpty(this.class) &&
        options.doNotShowIfSameNagClassAlreadyShown === true;

    const onNagShownAndPopStateTriggered = () => {
        this.dispose();
        window.removeEventListener('popstate', onNagShownAndPopStateTriggered);
    };

    window.addEventListener('popstate', onNagShownAndPopStateTriggered);
};

/**
 * @enum
 * @static
 * */
Nag.NAG_LOCAL_EVENTS = new Odigeo.Enum(['onAcceptNag', 'onRejectNag', 'onNagDisposed']);
/**
 * @enum
 * @static
 * */
Nag.NAG_TYPE = new Odigeo.Enum(['reject', 'continuance', 'smartContinuance', 'confirmation']);
/**
 * @enum
 * @static
 * */
Nag.NAG_STATES = new Odigeo.Enum(['accept', 'reject', 'appears', 'tryclose']);

/**
 * @static
 * */
Nag.NAG_EVENTS_ID = 'nag';
Nag.NAG_TRACKING_ACTION = 'nags';

/**
 * @static
 * @param {string} nagClass
 * @return {Object} the session storage data
 * */
Nag.getNagClassSessionStorageData = (nagClass) => {
    return Odigeo.Utils.parseJson(Odigeo.Session.get(SESSION_STORAGE_NAG_CLASS_PREFIX + nagClass));
};

Nag.setNagClassSessionStorageData = (nagClass, data) => {
    if (!Odigeo._.isUndefined(data) && Odigeo._.isObject(data)) {
        Odigeo.Session.set(SESSION_STORAGE_NAG_CLASS_PREFIX + nagClass, JSON.stringify(data));
    }
};

/**
 * @static
 * @param {string} nagClass
 * @return {boolean} whether or not the nag class has already been shown
 * */
Nag.isNagClassAlreadyBeenShown = (nagClass) => {
    const sessionStorageData = Nag.getNagClassSessionStorageData(nagClass);
    return !Odigeo._.isEmpty(sessionStorageData) && sessionStorageData.classShown === true;
};

Nag.defaultTransition = (nag, done) => {
    done(nag);
};


Odigeo._.extend(Nag.prototype, {
    initSessionStorageData() {
        const hasNagClassBeenShownPreviously = false;
        const tmpData = Odigeo._.isEmpty(this.class) ? null : Odigeo._.extend({
            classShown: hasNagClassBeenShownPreviously,
        }, Nag.getNagClassSessionStorageData(this.class));

        if (!Odigeo._.isEmpty(this.sessionStorageInstanceID)) {
            const hasNagInstanceBeenShownPreviously = false;
            tmpData[this.sessionStorageInstanceID] = {
                shown: hasNagInstanceBeenShownPreviously,
            };
        }

        this.sessionStorageData = tmpData;
    },

    generateHtml() {
        if (Odigeo._.isUndefined(this.$nagContainer)) {
            this.$nagContainer = $(this.templateBuilder({
                translations: this.translations,
            }));

            this.$buttonAccept = this.$nagContainer.find(this.selectors.acceptButtonSelector);
            this.$buttonReject = this.$nagContainer.find(this.selectors.rejectButtonSelector);
            if (!Odigeo._.isUndefined(this.actionLabel)) {
                this.$buttonAccept.attr('data-id', this.actionLabel);
            }

            if (!Odigeo._.isUndefined(this.ignoreLabel)) {
                this.$buttonReject.attr('data-id', this.ignoreLabel);
            }

            this.bindNagButton({
                $btn: this.$buttonAccept,
                trackingState: Nag.NAG_STATES.accept,
                callback: Odigeo._.bind(this.triggerNagAction, this, Nag.NAG_LOCAL_EVENTS.onAcceptNag, this.type),
                transition: this.transitions.accept,
            });

            this.bindNagButton({
                $btn: this.$buttonReject,
                trackingState: Nag.NAG_STATES.reject,
                callback: Odigeo._.bind(this.triggerNagAction, this, Nag.NAG_LOCAL_EVENTS.onRejectNag, this.type),
            });

            this.bindNagEvent({
                $elem: this.$nagContainer.find('.nag_content'),
            });
            this.bindNagEvent({
                $elem: this.$nagContainer,
                trackingState: Nag.NAG_STATES.tryclose,
            });
            if (!Odigeo._.isUndefined(this.className)) {
                this.$nagContainer.find('.nag_content').addClass(this.className);
            }
            if (!Odigeo._.isUndefined(this.label)) {
                this.$nagContainer.find('.nag_content').attr('data-id', this.label);
            }

            this.$parent.append(this.$nagContainer);
        }
    },

    triggerNagAction(eventName, type) {
        this.trigger(eventName, type);
    },

    clean() {
        this.type = undefined;

        this.lightBox.off();
        this.lightBox = undefined;

        this.$nagContainer.empty();
        this.$nagContainer = undefined;

        this.$buttonAccept.off();
        this.$buttonAccept = undefined;

        this.$buttonReject.off();
        this.$buttonReject = undefined;
    },

    /**
     * Show nag
     * @param {Nag.NAG_TYPE} nagType the type of the nag that will be shown
     * @return {boolean} true if nag is shown, false otherwise
     * */
    show(nagType) {
        this.type = Nag.NAG_TYPE[nagType];

        const isAValidNagType = !Odigeo._.isUndefined(this.type);
        const hasNagAlreadyBeenShown = (this.showThisInstanceJustOnce && this.hasBeenInstanceShown) ||
            (this.doNotShowIfSameNagClassAlreadyShown && this.hasBeenNagClassShown);

        if (!isAValidNagType || hasNagAlreadyBeenShown) {
            return false;
        }

        if (Odigeo._.isUndefined(this.$nagContainer)) {
            const classNameType = `.${nagType}_class`;
            this.generateHtml();
            trackOncePerPolicy($(classNameType));
        }

        this.lightBox = new Odigeo.UIElements.Lightbox(this.$nagContainer, {
            closeOnOuterClick: false,
            focusClass: '.nag_btn_reject',
        });

        this.hasBeenInstanceShown = true;

        this.lightBox.toggle(true);

        this.trackByState(Nag.NAG_STATES.appears);

        return true;
    },

    dispose() {
        if (!Odigeo._.isUndefined(this.lightBox)) {
            this.lightBox.on('lightbox:remove', Odigeo._.bind(this.onNagDisposed, this));
            this.lightBox.remove();
        }
    },

    onNagDisposed() {
        this.clean();
        this.trigger(Nag.NAG_LOCAL_EVENTS.onNagDisposed);
    },

    bindNagEvent({$elem, trackingState = null, eventType = Odigeo.Events.tap, callback = Odigeo._.noop, transition = Nag.defaultTransition}) {
        $elem = ($elem instanceof $) ? $elem : $($elem);
        $elem.on(eventType, Odigeo._.bind((event) => {
            event.preventDefault();
            event.stopPropagation();
            trackClickEvent(event);
            this.trackByState(trackingState);
            transition(this, callback);
        }, this));
    },
    bindNagButton({$btn, trackingState, callback, transition}) {
        const wrappedCallbackWithDispose = (nag) => {
            this.dispose();
            callback(nag);
        };
        this.bindNagEvent({
            $elem: $btn,
            callback: wrappedCallbackWithDispose,
            trackingState,
            transition,
        });
    },

    updateTracking(trackingData, trackingPage) {
        this.tracking = Odigeo._.isPlainObject(trackingData) && !Odigeo._.isEmpty(trackingData) ? trackingData : this.tracking;
        this.tracking.page = Odigeo._.isString(trackingPage) ? trackingPage : this.tracking.page;
    },

    getTrackingData(trackingProperty) {
        const tracking = this.tracking[trackingProperty];
        const page = this.tracking.page || Odigeo.History.getPageNameFromFragment();

        return {
            action: tracking.action || Nag.NAG_TRACKING_ACTION,
            label: tracking.label,
            page: page,
        };
    },

    trackByState(state) {
        if (state && !Odigeo._.isUndefined(Nag.NAG_STATES[state]) && !Odigeo._.isEmpty(this.tracking[state])) {
            const t = this.getTrackingData(state);
            trackingUtils.trackEvent(t.action, t.label, t.page);
        }
    },
}, Odigeo.Events);

Object.defineProperty(Nag.prototype, 'sessionStorageData', {
    get() {
        return Nag.getNagClassSessionStorageData(this.class);
    },
    set(data) {
        Nag.setNagClassSessionStorageData(this.class, data);
    },
});

Object.defineProperty(Nag.prototype, 'hasBeenNagClassShown', {
    get() {
        return !Odigeo._.isEmpty(this.sessionStorageData) && this.sessionStorageData.classShown === true;
    },
    set() {
        if (!Odigeo._.isEmpty(this.sessionStorageData)) {
            const tmpData = this.sessionStorageData;
            tmpData.classShown = true;
            this.sessionStorageData = tmpData;
        }
    },
});

Object.defineProperty(Nag.prototype, 'hasBeenInstanceShown', {
    get() {
        return !Odigeo._.isEmpty(this.sessionStorageData) &&
            !Odigeo._.isEmpty(this.sessionStorageData[this.sessionStorageInstanceID]) &&
            this.sessionStorageData[this.sessionStorageInstanceID].shown === true;
    },
    set() {
        if (!Odigeo._.isEmpty(this.sessionStorageData)) {
            const tmpData = this.sessionStorageData;
            if (!Odigeo._.isEmpty(tmpData[this.sessionStorageInstanceID])) {
                tmpData[this.sessionStorageInstanceID].shown = true;
                this.sessionStorageData = tmpData;
            }

            this.hasBeenNagClassShown = true;
        }
    },
});

module.exports = Nag.prototype.constructor = Nag;
